import React from 'react';
import { User } from '../../types/user';
import { EnhancedUser } from '../../types/enhancedUser';
import DataGrid, { GridColumn, GridRow } from '../data/DataGrid';

interface UserTableProps {
  users: EnhancedUser[];
  loading: boolean;
  currentUserId: string | null;
  onEdit: (user: EnhancedUser) => void;
  onChangePassword: (user: EnhancedUser) => void;
  onDelete: (id: string) => void;
  onAssignFeatures: (user: EnhancedUser) => void;
}

const UserTable: React.FC<UserTableProps> = ({ 
  users, 
  loading, 
  currentUserId,
  onEdit, 
  onChangePassword,
  onDelete,
  onAssignFeatures
}) => {
  return (
    <DataGrid
      columns={[
        {
          field: 'id',
          headerName: 'ID',
          width: 60,
          type: 'number'
        },
        {
          field: 'name',
          headerName: 'Name',
          width: 160
        },
        {
          field: 'email',
          headerName: 'Email',
          width: 200
        },
        {
          field: 'role',
          headerName: 'Role',
          width: 280,
          renderCell: (row: GridRow) => {
            const user = row as EnhancedUser;
            
            // Determine the primary role display and color scheme:
            // Dark Red (#8B0000) - Super Admin
            // Purple (#464eb8) - Organization Admin  
            // Blue (#0066cc) - Group Admin
            // Forest Green (#228B22) - Organization Scope Roles
            // Dark Orange (#FF8C00) - Hierarchy Node Scope Roles
            // Dark Orchid (#9932CC) - Location Scope Roles
            // Sky Blue (#87CEEB) - Regular Users
            let primaryRole = '';
            let roleColor = 'rgba(0, 200, 255, 0.2)';
            
            if (user.is_super_admin) {
              primaryRole = 'Super Admin';
              roleColor = '#8B0000'; // Dark red for super admin
            } else if (user.is_organization_admin) {
              primaryRole = 'Organization Admin';
              roleColor = '#464eb8'; // Purple for organization admin
            } else if (user.role === 'group_admin') {
              primaryRole = 'Group Admin';
              roleColor = '#0066cc'; // Blue for group admin
            } else if (user.roles && user.roles.length > 0) {
              // Show the first dynamic role with context and scope-specific colors
              const firstRole = user.roles[0];
              
              // Set color based on scope type
              if (firstRole.scope_type === 'organization') {
                roleColor = '#228B22'; // Forest green for organization roles
              } else if (firstRole.scope_type === 'hierarchy_node') {
                roleColor = '#FF8C00'; // Dark orange for hierarchy node roles
              } else if (firstRole.scope_type === 'location') {
                roleColor = '#9932CC'; // Dark orchid for location roles
              } else {
                roleColor = '#708090'; // Slate gray for unknown scope
              }
              
              // Format role name with context
              if (firstRole.scope_type === 'hierarchy_node' && firstRole.hierarchy_node_name) {
                // Include parent hierarchy node if available
                if (firstRole.hierarchy_node_parent_name) {
                  primaryRole = `${firstRole.name} (${firstRole.hierarchy_node_parent_name} → ${firstRole.hierarchy_node_name})`;
                } else {
                  primaryRole = `${firstRole.name} (${firstRole.hierarchy_node_name})`;
                }
              } else if (firstRole.scope_type === 'location' && firstRole.location_name) {
                primaryRole = `${firstRole.name} (${firstRole.location_name})`;
              } else if (firstRole.scope_type === 'organization' && firstRole.organization_name) {
                primaryRole = `${firstRole.name} (${firstRole.organization_name})`;
              } else {
                primaryRole = firstRole.name;
              }
            } else {
              primaryRole = user.role?.replace('_', ' ') || 'User';
              roleColor = '#87CEEB'; // Sky blue for regular users
            }
            
            // Create tooltip text with all role information
            const tooltipText = user.roles && user.roles.length > 0 
              ? user.roles.map(role => {
                  let roleDesc = role.name;
                  if (role.scope_type === 'hierarchy_node' && role.hierarchy_node_name) {
                    if (role.hierarchy_node_parent_name) {
                      roleDesc += ` (Hierarchy: ${role.hierarchy_node_parent_name} → ${role.hierarchy_node_name})`;
                    } else {
                      roleDesc += ` (Hierarchy: ${role.hierarchy_node_name})`;
                    }
                  } else if (role.scope_type === 'location' && role.location_name) {
                    roleDesc += ` (Location: ${role.location_name})`;
                  } else if (role.scope_type === 'organization' && role.organization_name) {
                    roleDesc += ` (Organization: ${role.organization_name})`;
                  }
                  return roleDesc;
                }).join('\n')
              : primaryRole;

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span 
                  style={{ 
                    background: roleColor,
                    color: 'white', 
                    padding: '4px 8px', 
                    borderRadius: '12px', 
                    fontSize: '11px',
                    textTransform: 'capitalize',
                    display: 'inline-block',
                    cursor: 'help'
                  }}
                  title={tooltipText}
                >
                  {primaryRole}
                </span>
                {user.roles && user.roles.length > 1 && (
                  <span 
                    style={{ 
                      fontSize: '10px', 
                      color: '#666',
                      fontStyle: 'italic',
                      cursor: 'help'
                    }}
                    title={`Additional roles:\n${user.roles.slice(1).map(role => {
                      let roleDesc = role.name;
                      if (role.scope_type === 'hierarchy_node' && role.hierarchy_node_name) {
                        if (role.hierarchy_node_parent_name) {
                          roleDesc += ` (Hierarchy: ${role.hierarchy_node_parent_name} → ${role.hierarchy_node_name})`;
                        } else {
                          roleDesc += ` (Hierarchy: ${role.hierarchy_node_name})`;
                        }
                      } else if (role.scope_type === 'location' && role.location_name) {
                        roleDesc += ` (Location: ${role.location_name})`;
                      } else if (role.scope_type === 'organization' && role.organization_name) {
                        roleDesc += ` (Organization: ${role.organization_name})`;
                      }
                      return roleDesc;
                    }).join('\n')}`}
                  >
                    +{user.roles.length - 1} more
                  </span>
                )}
              </div>
            );
          }
        },
        {
          field: 'organization_name',
          headerName: 'Organization',
          width: 130,
          renderCell: (row: GridRow) => {
            const user = row as EnhancedUser;
            return (
              <span title={`ID: ${user.organization_id || 'None'}`}>
                {user.organization_name || '-'}
              </span>
            );
          }
        },
        {
          field: 'groupAdminName',
          headerName: 'Group Admin',
          width: 120,
          renderCell: (row: GridRow) => (
            <span>{row.groupAdminName || '-'}</span>
          )
        },
        {
          field: 'total_ai_requests',
          headerName: 'AI Requests',
          width: 100,
          type: 'number',
          renderCell: (row: GridRow) => (
            <span>{row.total_ai_requests || 0}</span>
          )
        },
        {
          field: 'total_tokens_used',
          headerName: 'Tokens Used',
          width: 110,
          type: 'number',
          renderCell: (row: GridRow) => (
            <span>{row.total_tokens_used ? row.total_tokens_used.toLocaleString() : 0}</span>
          )
        },
        {
          field: 'unique_dealers_count',
          headerName: 'Dealers',
          width: 80,
          type: 'number',
          renderCell: (row: GridRow) => (
            <span>{row.unique_dealers_count || 0}</span>
          )
        },
        {
          field: 'unique_kpis_count',
          headerName: 'KPIs',
          width: 70,
          type: 'number',
          renderCell: (row: GridRow) => (
            <span>{row.unique_kpis_count || 0}</span>
          )
        },
        {
          field: 'last_ai_request_at',
          headerName: 'Last AI Request',
          width: 130,
          type: 'date',
          renderCell: (row: GridRow) => (
            <span>{row.last_ai_request_at ? new Date(row.last_ai_request_at).toLocaleDateString() : '-'}</span>
          )
        },
        {
          field: 'created_at',
          headerName: 'Created',
          width: 100,
          type: 'date',
          renderCell: (row: GridRow) => (
            <span>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}</span>
          )
        },
        {
          field: 'actions',
          headerName: 'Actions',
          width: 380,
          renderCell: (row: GridRow) => (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap', alignItems: 'center' }}>
              <button 
                className="secondary-button"
                onClick={() => onEdit(row as EnhancedUser)}
                style={{ fontSize: '10px', padding: '3px 6px', minWidth: '50px' }}
              >
                Edit
              </button>
              <button 
                className="secondary-button"
                onClick={() => onChangePassword(row as EnhancedUser)}
                style={{ fontSize: '10px', padding: '3px 6px', minWidth: '60px' }}
              >
                Password
              </button>
              <button 
                className="secondary-button"
                onClick={() => onAssignFeatures(row as EnhancedUser)}
                style={{ 
                  fontSize: '10px', 
                  padding: '3px 6px',
                  minWidth: '60px',
                  backgroundColor: '#228B22',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px'
                }}
                title="Assign feature roles to this user"
              >
                Features
              </button>
              <button 
                className="delete-button"
                onClick={() => onDelete(String(row.id))}
                disabled={String(row.id) === String(currentUserId)}
                title={String(row.id) === String(currentUserId) ? "You cannot delete your own account" : ""}
                style={{ 
                  fontSize: '10px', 
                  padding: '3px 6px',
                  minWidth: '50px',
                  borderRadius: '4px', 
                  border: 'none', 
                  cursor: String(row.id) === String(currentUserId) ? 'not-allowed' : 'pointer',
                  opacity: String(row.id) === String(currentUserId) ? 0.5 : 1
                }}
              >
                Delete
              </button>
            </div>
          )
        }
      ]}
      rows={users.map(user => ({
        ...user, // Include all user properties
        groupAdminName: user.groupAdminName
      }))}
      height="100%"
      loading={loading}
      className="users-grid"
    />
  );
};

export default UserTable; 